import React from 'react';
import styled from 'styled-components';
import {
  AllieWizardProps,
  WizardAction,
  WizardButtonProps,
  WizardContextProps,
  WizardGoToButtonProps,
  WizardPagesProps,
  WizardState,
} from './Wizard.types';
import { AllieButton } from '../Buttons/Button';
// eslint-disable-next-line max-len
import { StyledGoBackButton } from '../../views/components/EfficiencyWizard/EfficiencyWizard.styles';
import { AllieChevronBackOutline } from '../../styling/icons';

export const StyledButtonCancel = styled.div`
  --background: var(--allie-color-primary-5) !important;
  --color: var(--allie-color-primary-70) !important;
  --color-hover: var(--allie-color-primary-50) !important;
`;

const WizardContext = React.createContext<WizardContextProps>({
  activePageIndex: 0,
  steps: 0,
  goNextPage: () => {
    return;
  },
  goPrevPage: () => {
    return;
  },
  goToPage: () => {
    return;
  },
  setSteps: () => {
    return;
  },
});

export const useWizardContext = () => {
  const context = React.useContext(WizardContext);
  if (!context) {
    throw new Error(
      `Un componente compuesto de Wizard no puede ser renderizado fuera del Wizard padre`
    );
  }
  return context;
};

export const useWizardProgress = () => {
  const { activePageIndex, steps } = useWizardContext();
  return {
    currentIndex: activePageIndex,
    steps,
  };
};

export const useWizardButtons = () => {
  const { goNextPage, goPrevPage, goToPage, activePageIndex, steps } = useWizardContext();
  return {
    goNextPage,
    goPrevPage,
    goToPage,
    activePageIndex,
    steps,
  };
};

const reducer = (state: WizardState, action: WizardAction) => {
  const { steps, activePageIndex } = state;
  switch (action.type) {
    case 'NEXT_PAGE':
      const newIndex = activePageIndex + 1;
      if (newIndex < steps) {
        return { ...state, activePageIndex: newIndex };
      }
      return state;
    case 'PREV_PAGE':
      if (activePageIndex > 0) {
        return { ...state, activePageIndex: activePageIndex - 1 };
      }
      return state;
    case 'GO_TO_PAGE':
      return { ...state, activePageIndex: action.payload };
    case 'SET_STEP_COUNT':
      return { ...state, steps: action.payload };
    default:
      return state;
  }
};
const initialState = {
  activePageIndex: 0,
  steps: 0,
};

export const AllieWizard = ({
  children,
  className,
  initialContextState = {
    activePageIndex: 0,
    steps: 0,
  },
}: AllieWizardProps) => {
  const [state, dispatch] = React.useReducer(reducer, initialContextState);

  const goNextPage = React.useCallback(() => {
    dispatch({ type: 'NEXT_PAGE' });
  }, []);

  const goPrevPage = React.useCallback(() => {
    dispatch({ type: 'PREV_PAGE' });
  }, []);

  const goToPage = React.useCallback((pageIndex: number) => {
    dispatch({ type: 'GO_TO_PAGE', payload: pageIndex });
  }, []);

  const setSteps = React.useCallback(
    (steps: number) => {
      dispatch({ type: 'SET_STEP_COUNT', payload: steps });
    },
    [dispatch]
  );
  const { activePageIndex, steps } = state;

  const context = {
    activePageIndex,
    steps,
    goNextPage,
    goPrevPage,
    goToPage,
    setSteps,
  };
  return (
    <WizardContext.Provider value={context}>
      <div className={`allie-wizard ${className} ${className}-step${activePageIndex}`}>
        {children}
      </div>
    </WizardContext.Provider>
  );
};

const WizardPages = ({ children, style, ...props }: WizardPagesProps) => {
  const { setSteps, activePageIndex } = React.useContext(WizardContext);
  const pages = React.Children.toArray(children);

  const steps = React.Children.count(children);

  React.useEffect(() => {
    setSteps(steps);
  }, [steps, setSteps]);

  const currentPage = pages[activePageIndex];
  return (
    <div
      {...props}
      style={{
        flexGrow: 1,
        flexShrink: 1,
        overflow: 'auto',
        padding: '0 24px',
        boxSizing: 'border-box',
        ...style,
      }}
    >
      {currentPage}
    </div>
  );
};

const WizardButtonPrev = ({ children, ...props }: WizardButtonProps) => {
  const { goPrevPage, activePageIndex } = useWizardContext();

  return activePageIndex === 0 ? null : (
    <StyledGoBackButton
      allieColor="neutral"
      allieSize="small"
      corner="rounded-edges"
      slot="icon-only"
      {...props}
      onclick={goPrevPage}
    >
      {children ? (
        children
      ) : (
        <div style={{ display: 'flex' }}>
          <AllieChevronBackOutline size="large" />
        </div>
      )}
    </StyledGoBackButton>
  );
};

const WizardButtonNext = ({ children, ...props }: WizardButtonProps) => {
  const { goNextPage, activePageIndex, steps } = useWizardContext();

  return activePageIndex < steps - 1 ? (
    <AllieButton allieSize="small" corner="rounded-edges" {...props} onclick={goNextPage}>
      {children}
    </AllieButton>
  ) : null;
};

const WizardButtonGoTo = ({ page, children, ...props }: WizardGoToButtonProps) => {
  const { goToPage } = useWizardContext();

  return (
    <AllieButton
      allieSize="small"
      corner="rounded-edges"
      {...props}
      onclick={() => goToPage(page)}
    >
      {children}
    </AllieButton>
  );
};

const WizardButtonCancel = ({ children, ...props }: WizardButtonProps) => {
  return (
    <AllieButton allieSize="small" corner="rounded-edges" {...props}>
      {children}
    </AllieButton>
  );
};

const WizardButtonConfirm = ({ children, ...props }: WizardButtonProps) => {
  const { activePageIndex, steps } = useWizardContext();

  return activePageIndex === steps - 1 ? (
    <AllieButton allieSize="small" corner="rounded-edges" {...props}>
      {children}
    </AllieButton>
  ) : null;
};

export const useWizardPages = (totalSteps: number) => {
  const { setSteps, activePageIndex } = useWizardContext();
  React.useEffect(() => {
    setSteps(totalSteps);
  }, [totalSteps, setSteps]);

  return {
    activePageIndex,
  };
};

AllieWizard.Pages = WizardPages;
AllieWizard.ButtonNext = WizardButtonNext;
AllieWizard.ButtonPrev = WizardButtonPrev;
AllieWizard.ButtonGoTo = WizardButtonGoTo;
AllieWizard.ButtonCancel = WizardButtonCancel;
AllieWizard.ButtonConfirm = WizardButtonConfirm;

export default AllieWizard;
